import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnswerChoices from './answer-choices';
import AnswerMessage from './answer-message';
import AssistantTypingIndicator from './assistant-typing-indicator';
import InvalidQueryMessage from './invalid-query-message';
import LiveChatMessage from './live-chat-message';
import NoAnswerMessage from './no-answer-message';
import SystemMessage from './system-message';
import UserMessage from './user-message';
import {
  IModalContent,
  INlpRequest,
  IQuestionOption,
  MessageType,
  Modals,
  TNullable,
} from '../../common/types';
import useChat from '../../hooks/use-chat';
import { cn, shouldUseMaxHeight } from '../../lib/utils';
import { CURRENT_INSTANCE } from '../../tenant';
import ConsentOverlay from '../agreement/consent-overlay';
import GenericModal from '../common/generic-modal';

interface ChatMessageListProps {
  request: TNullable<INlpRequest>;
  onShowMoreAnswers: (messageId: number) => void;
  onSelectChoice: (choice: IQuestionOption | undefined) => void;
  isLoading: boolean;
}

function ChatMessageList(props: ChatMessageListProps) {
  const { t } = useTranslation();
  const { messages, showAgreementBanner } = useChat();
  const [modalContent, setModalContent] = useState<IModalContent>();
  const chatStreamContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatStreamContainerRef?.current?.scroll({
      top: chatStreamContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [props.isLoading, chatStreamContainerRef, messages]);

  function handleModalShow(type: Modals, userQuery?: string) {
    if (Modals.MedicalInfo === type) {
      const text = CURRENT_INSTANCE.content.medicalInfo;
      const title = t('chat.medical_info.title');
      setModalContent({ text, title, userQuery });
      return;
    }
    setModalContent(undefined);
  }

  return (
    <div
      className={cn(
        'w-full mt-4 overflow-y-auto rounded-2xl border border-slate-200/70 bg-white/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] custom-scroll',
        shouldUseMaxHeight() && '!max-h-[600px] sm:!max-h-[500px]',
        !shouldUseMaxHeight() && 'max-h-[calc(100vh-260px)] sm:max-h-[calc(100vh-230px)]'
      )}
      ref={chatStreamContainerRef}
    >
      {showAgreementBanner && <ConsentOverlay />}
      <div className="flex flex-col justify-start">
        {messages.map((message, index) => {
          const lastUserQuery = [...messages.slice(0, index + 1)]
            .reverse()
            .find((item) => item.type === MessageType.UserEntry)?.message;

          if (message.type === MessageType.UserEntry) {
            return <UserMessage message={message.message} key={message.id} />;
          }
          if (message.type === MessageType.LiveChatUserEntry) {
            return <UserMessage message={message.message} key={message.id} />;
          }
          if (
            message.type === MessageType.LiveChatOperatorMessage ||
            message.type === MessageType.LiveChatNotice
          ) {
            return <LiveChatMessage message={message} key={message.id} />;
          }
          if (
            message.type === MessageType.Message ||
            message.type === MessageType.Error ||
            message.type === MessageType.DisabledTopicMessage
          ) {
            return (
              <SystemMessage
                message={message}
                key={message.id}
                showModal={handleModalShow}
                userQuery={lastUserQuery}
              />
            );
          }
          if (message.type === MessageType.Response) {
            return (
              <AnswerMessage
                key={message.id}
                message={message}
                onShowMoreAnswers={props.onShowMoreAnswers}
                showModal={handleModalShow}
                hasMoreOptions={message.choices && message.choices.length > 1}
                userQuery={lastUserQuery}
              />
            );
          }
          if (message.type === MessageType.ChoiceResponse) {
            return (
              <AnswerChoices
                key={message.id}
                message={message}
                onSelectChoice={props.onSelectChoice}
              />
            );
          }
          if (
            message.type === MessageType.NoAnswerResponse ||
            message.type === MessageType.ForceMedInfo
          ) {
            return <NoAnswerMessage key={message.id} userQuery={lastUserQuery} />;
          }
          if (message.type === MessageType.InvalidQuery) {
            return <InvalidQueryMessage key={message.id} userQuery={lastUserQuery} />;
          }
          return null;
        })}
        {modalContent && (
          <GenericModal
            open={!!modalContent}
            onOpenChange={() => setModalContent(undefined)}
            title={modalContent.title}
            description={modalContent.text}
            userQuery={modalContent.userQuery}
          />
        )}
        {props.isLoading && <AssistantTypingIndicator />}
      </div>
    </div>
  );
}

export default ChatMessageList;
